import Box from '@mui/material/Box';

/**
 * The two ways a screen tells someone what their account can do.
 *
 * Both are courtesy, not security — the API refuses the same things regardless.
 * The point is that the UI shouldn't offer a control and then hand back a 403.
 */

/**
 * Stands in for something this account can't reach — a channel outside its scope,
 * or an Admin-only area. Pass `channel` for the first, `area` for the second.
 */
export function NoAccess({ channel, area }) {
  if (area) {
    return (
      <Box
        sx={{
          background: 'var(--card)',
          border: '1px dashed var(--border)',
          borderRadius: 'var(--radius)',
          p: '38px 28px',
          textAlign: 'center',
          color: 'var(--ink-2)',
        }}
      >
        <Box sx={{ fontSize: 30, mb: 1.5 }}>🔒</Box>
        <Box sx={{ fontFamily: 'var(--disp)', fontSize: 17, fontWeight: 700, color: 'var(--ink)' }}>
          {area} is Admin only
        </Box>
        <Box sx={{ fontSize: 13.5, mt: 1, maxWidth: 460, mx: 'auto', lineHeight: 1.6 }}>
          Managing the team, commission tiers and the platform's pick-lists is restricted to Admin accounts.
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        background: 'var(--card)',
        border: '1px dashed var(--border)',
        borderRadius: 'var(--radius)',
        p: '38px 28px',
        textAlign: 'center',
        color: 'var(--ink-2)',
      }}
    >
      <Box sx={{ fontSize: 30, mb: 1.5 }}>🔒</Box>
      <Box sx={{ fontFamily: 'var(--disp)', fontSize: 17, fontWeight: 700, color: 'var(--ink)' }}>
        No access to {channel?.name || 'this channel'}
      </Box>
      <Box sx={{ fontSize: 13.5, mt: 1, maxWidth: 460, mx: 'auto', lineHeight: 1.6 }}>
        Your account isn't scoped to {channel?.full || 'this channel'}. An Admin can widen your channel access from
        Settings · Users.
      </Box>
    </Box>
  );
}

/** A quiet line at the top of a screen whose write controls are hidden. */
export function ReadOnlyNotice({ role, what = 'these records' }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: '9px 13px',
        mb: 2,
        borderRadius: '10px',
        background: 'var(--card-2)',
        border: '1px solid var(--border)',
        color: 'var(--ink-2)',
        fontSize: 12.5,
      }}
    >
      <Box component="span" aria-hidden>👁</Box>
      <Box>
        {/* "{what}" is sometimes singular, sometimes plural — end on a clause that fits both */}
        <b>{role || 'This account'}</b> has read-only access to {what} — you can view and export, but not make changes.
      </Box>
    </Box>
  );
}

export default NoAccess;
